<template>
  <div class="buybox__gallery">
    <div class="buybox__gallery-content">
      <div class="bg__thumbnails">
        <div class="bg__thumbnail-wrapper">
          <div v-for="(image,index) in carouselImages" :key="index" class="bg__thumbnail">
            <transition name="fade" mode="out-in">
              <ImageField :key="image.src" class="bg__thumbnail-image" :obj="{'url':image.src,'alt':image.alt,'width':128,'height':128}" width-param="width" sizes="64px" :isLoadingLazy="true"/>
            </transition>
          </div>
        </div> 
      </div>
      <div class="bg__images">
        <div v-if="isFeatureTag" class="bg__images-tag" :style=" { '--bg-tag': isFeatureTag.color }">
          {{ isFeatureTag.name }}
        </div>
        <div class="bg__slide-wrapper">
          <div v-for="(image,index) in carouselImages" :key="index" class="bg__slide">
            <span class="bg__image-count text-sm">{{index+1}} / {{carouselImages.length}}</span>
            <transition name="fade" mode="out-in">
              <ImageField :key="image.src" class="bg__image" width="800" :obj="{'url':image.src,'alt':image.alt,'width':1600,'height':1600}" width-param="width" sizes="(max-width: 800px) 100vw, 800px" :load="index === 0 ? true: false" :isLoadingLazy="true" @click="showLightbox(index)"/>
            </transition>
          </div>
        </div>
      </div>

      <VueEasyLightbox
        :visible="visible"
        :imgs="lightboxImgs"
        :index="index"
        @hide="visible = false"
        min-zoom="1"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, onMounted, watch, toRefs} from 'vue';
  import ImageField from "../../global/image.vue";
  import Swiper from 'swiper';
  import { Navigation, EffectFade, Thumbs } from 'swiper/modules';
  import VueEasyLightbox from 'vue-easy-lightbox'

  const props = defineProps({
    product: {
      type: Object,
      default: () => ({})
    },

    selectedVariant: {
      type: Object,
      default: () => ({})
    },

    isMobile: {
      type: Boolean,
      default: false, 
    },
  })

  const { product, selectedVariant, isMobile } = toRefs(props);
  const thumbnailSwiper = ref(null);
  const gallerySwiper = ref(null);

  onMounted(() => {
    initializeSwipers();
  });

  // onUpdated(() => {
  //   setTimeout(() => {
  //     destroySwipers();
  //     initializeSwipers();
  //   }, 100);
  // });

  const carouselImages = computed(() => {
    const { images, options } = product.value;
    
    // Extract first and second options
    const firstOption = (options.find(option => option.position === 1) || null);
    const secondOption = (options.find(option => option.position === 2) || null);

    // Build regex pattern based on firstOption and secondOption
    const imgPattern = imagePattern(firstOption, secondOption);

    // Filter images based on the regex pattern
    const matchingImages = images.filter(image => imgPattern.test(image.alt.replace(/&amp;/g, '&')));

    // Separate images with "last" in alt text
    const imagesWithLast = matchingImages.filter(image => /\/last$/i.test(image.alt));
    const imagesWithoutLast = matchingImages.filter(image => !/\/last$/i.test(image.alt));

    // Concatenate images with "last" at the end
    if (matchingImages.length > 0) {
      return [...imagesWithoutLast, ...imagesWithLast];
    } else if (selectedVariant.value.images.length > 0)  {
      return selectedVariant.value.images;
    } else {
      return product.value.images;
    }
  });

  const isFeatureTag = computed(() => {
    return selectedVariant.value.feature_tag;
  })

  const thumbnailCarousel = computed(() => {
    return {
      modules: [Navigation, EffectFade, Thumbs],
      slidesPerView: 6,
      spaceBetween: 12,
      direction: 'vertical',
      wrapperClass: 'bg__thumbnail-wrapper',
      slideClass: 'bg__thumbnail',
      threshold: 1000
    };
  });

  const galleryCarousel = computed(() => {
    return {
      modules: [Navigation, EffectFade, Thumbs],
      slidesPerView: 1.2,
      spaceBetween: 12,
      wrapperClass: 'bg__slide-wrapper',
      slideClass: 'bg__slide',
      thumbs: {
        swiper: thumbnailSwiper.value
      },
      breakpoints: {
        1024:{
          slidesPerView: 1,
          spaceBetween: 4
        } 
      },
    }
  })

  const initializeSwipers = () => {
    thumbnailSwiper.value = new Swiper('.bg__thumbnails', thumbnailCarousel.value);
    gallerySwiper.value = new Swiper('.bg__images', galleryCarousel.value);
    // gallerySwiper.value.slideTo(0, 500);
  };

  const destroySwipers = () => {
    thumbnailSwiper.value?.destroy();
    gallerySwiper.value?.destroy();
  };

  const imagePattern = (firstOption, secondOption) => {
    const firstOptionLower = firstOption.selected.toLowerCase();
    const secondOptionLower = secondOption?.selected.toLowerCase();
    const commonPatterns = [`^${firstOptionLower}/all$`, `^${firstOptionLower}/all/last$`, `^all$`];

    const regexPattern = secondOption && !['boxes', 'tubs', 'bottles'].includes(secondOption.name.toLowerCase())
    ? [`^${firstOptionLower}/${secondOptionLower}$`, `^${firstOptionLower}/${secondOptionLower}/last$`, ...commonPatterns]
    : [`^${firstOptionLower}$`, `^${firstOptionLower}/last$`, ...commonPatterns];

    return new RegExp(regexPattern.join('|'), 'i');
  }

  watch(carouselImages, () => {
    destroySwipers();
    initializeSwipers();
  })

  const lightboxImgs = carouselImages.value.map(img => img.src)

  const visible = ref(false)
  const index = ref(0)

  const showLightbox = (clickedIndex) => {
    index.value = clickedIndex
    visible.value = true
  }
</script>